/**
 * API Configuration
 *
 * Centralized API configuration for the application
 */

export const API_CONFIG = {
  // User Management Service
  USER_MANAGEMENT: {
    BASE_URL: import.meta.env.VITE_USER_MANAGEMENT_API_URL || 'http://localhost:8080/v1/users',
    TIMEOUT: 30000,
  },

  // Publisher Service (to be implemented)
  PUBLISHER: {
    BASE_URL: import.meta.env.VITE_PUBLISHER_API_URL || 'http://localhost:8080/v1/publishers',
    TIMEOUT: 30000,
  },

  // Backoffice Service
  BACKOFFICE: {
    BASE_URL: import.meta.env.VITE_BACKOFFICE_API_URL || 'http://localhost:8080/api/v1',
    TIMEOUT: 30000,
  },

  // Legacy/Generic API
  BASE_URL: import.meta.env.VITE_API_URL || 'http://localhost:3000/api',

  // API timeout in milliseconds
  TIMEOUT: 30000,

  // API version
  VERSION: 'v1',

  // Default headers
  HEADERS: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
} as const;

/**
 * API Endpoints
 */
export const API_ENDPOINTS = {
  // ==================== User Management Service Endpoints ====================
  USER_MANAGEMENT: {
    // File Upload
    UPLOAD_BUSINESS_LICENSE: '/files/business-license',

    // Authentication
    LOGIN: '/login',
    REGISTER: '/register',
    LOGOUT: '/logout',
    REFRESH_TOKEN: '/refresh-token',
    VALIDATE_TOKEN: '/validate-token',
    FORGOT_PASSWORD: '/forgot-password',
    RESET_PASSWORD: '/reset-password',

    // Profile
    PROFILE: '/profile',
    UPDATE_PROFILE: '/profile',
    CHANGE_PASSWORD: '/profile/change-password',

    // Producer Invitation / Activation
    // Spec path is /users/producers/activate relative to the /v1/users base —
    // the doubled /users segment matches the deployed service (same as USER_ROLES).
    PRODUCERS_ACTIVATE: '/users/producers/activate',

    // Roles
    USER_ROLES: (userId: string) => `/users/${userId}/roles`,
    ASSIGN_ROLE: (userId: string) => `/users/${userId}/roles`,
    REMOVE_ROLE: (userId: string) => `/users/${userId}/roles`,
    ALL_ROLES: '/roles',

    // Users by Role (uses X-Role-Name header)
    USERS_BY_ROLE: '/',

    // Permissions
    USER_PERMISSIONS: (userId: string) => `/users/${userId}/permissions`,

    // Health
    HEALTH: '/health',
  },

  // ==================== Publisher Service Endpoints ====================
  PUBLISHER: {
    // Publishers
    PUBLISHERS: '/publishers',
    PUBLISHER_BY_ID: (publisherId: string) => `/publishers/${publisherId}`,
    PUBLISHER_BY_USER_ID: (userId: string) => `/publishers/user/${userId}`,

    // Books
    PUBLISHER_BOOKS: (publisherId: string) => `/publishers/${publisherId}/books`,
    BOOK_BY_ID: (bookId: string) => `/books/${bookId}`,
    BOOK_STATUS: (bookId: string) => `/books/${bookId}/status`,
    BOOK_LIFECYCLE: (bookId: string) => `/books/${bookId}/lifecycle`,

    // Attachments
    BOOK_ATTACHMENTS: (bookId: string) => `/books/${bookId}/attachments`,
    ATTACHMENT_BY_ID: (attachmentId: string) => `/attachments/${attachmentId}`,
    ATTACHMENT_DOWNLOAD: (attachmentId: string) => `/attachments/${attachmentId}/download`,

    // Bulk Upload Requests
    PUBLISHER_REQUESTS: (publisherId: string) => `/publishers/${publisherId}/requests`,
    REQUEST_BY_ID: (requestId: string) => `/requests/${requestId}`,
    REQUEST_BOOKS: (requestId: string) => `/requests/${requestId}/books`,

    // Contract Requests (matching API spec paths)
    CONTRACT_REQUESTS: (publisherId: string) => `/publishers/${publisherId}/contract-requests`,
    CONTRACT_REQUEST_BY_ID: (contractRequestId: string) => `/contract-requests/${contractRequestId}`,

    // Publisher Contracts (view + download own signed contracts)
    PUBLISHER_CONTRACTS: (publisherId: string) => `/publishers/${publisherId}/contracts`,
    PUBLISHER_CONTRACT_BY_ID: (publisherId: string, contractId: string) => `/publishers/${publisherId}/contracts/${contractId}`,
    PUBLISHER_CONTRACT_DOWNLOAD: (publisherId: string, contractId: string) => `/publishers/${publisherId}/contracts/${contractId}/download`,
    PUBLISHER_DOCUMENTS: (publisherId: string) => `/publishers/${publisherId}/documents`,
    PUBLISHER_DOCUMENT_BY_TYPE: (publisherId: string, documentType: string) => `/publishers/${publisherId}/documents/${documentType}`,
    DOCUMENT_DOWNLOAD: (documentId: string) => `/documents/${documentId}/download`,
  },

  // ==================== Backoffice Service Endpoints ====================
  BACKOFFICE: {
    // Books
    BOOKS: '/books',

    // Requests
    REQUESTS: '/requests',
    REQUEST_BY_ID: (requestId: string) => `/requests/${requestId}`,
    REQUEST_REVIEW_SHEET: (requestId: string) => `/requests/${requestId}/review-sheet`,

    // Book Reviewer
    BOOK_REVIEWER_TASKS: '/book-reviewer/tasks',
    BOOK_REVIEWER_TASKS_COMPLETED: '/book-reviewer/tasks/completed',
    BOOK_REVIEWER_TASK_ASSIGN: '/book-reviewer/tasks/assign',
    BOOK_REVIEWER_TASK_CLAIM: (bookId: string) => `/book-reviewer/tasks/${bookId}/claim`,
    BOOK_REVIEWER_TASK_UNCLAIM: (bookId: string) => `/book-reviewer/tasks/${bookId}/unclaim`,
    BOOK_REVIEWER_TASK_APPROVE: (bookId: string) => `/book-reviewer/tasks/${bookId}/approve`,
    BOOK_REVIEWER_TASK_EXCLUDE: (bookId: string) => `/book-reviewer/tasks/${bookId}/exclude`,

    // Manager
    MANAGER_REVIEW_TASKS: '/manager/review-tasks',
    MANAGER_REVIEW_TASK_BY_ID: (requestId: string) => `/manager/review-tasks/${requestId}`,
    MANAGER_REVIEW_TASK_CLAIM: (requestId: string) => `/manager/review-tasks/${requestId}/claim`,
    MANAGER_REVIEW_TASK_UNCLAIM: (requestId: string) => `/manager/review-tasks/${requestId}/unclaim`,
    MANAGER_REVIEW_TASK_APPROVE: (requestId: string) => `/manager/review-tasks/${requestId}/approve`,
    MANAGER_REVIEW_TASK_BOOK_APPROVE: (requestId: string, bookId: string) => `/manager/review-tasks/${requestId}/books/${bookId}/approve`,
    MANAGER_REVIEW_TASK_BOOK_REJECT: (requestId: string, bookId: string) => `/manager/review-tasks/${requestId}/books/${bookId}/reject`,
    MANAGER_TASKS_COMPLETED: '/manager/tasks/completed',

    // Content Rating Tasks
    CONTENT_RATING_TASKS: '/content-rating/tasks',
    CONTENT_RATING_TASKS_COMPLETED: '/content-rating/tasks/completed',
    CONTENT_RATING_TASKS_CLAIM: '/content-rating/tasks/claim',
    CONTENT_RATING_TASKS_UNCLAIM: '/content-rating/tasks/unclaim',
    CONTENT_RATING_TASKS_DOWNLOAD: '/content-rating/tasks/download',
    CONTENT_RATING_TASKS_APPROVE: '/content-rating/tasks/approve',
    CONTENT_RATING_TASKS_EXCLUDE: '/content-rating/tasks/exclude',

    // Producers
    PRODUCERS_LIST: '/producers',
    PRODUCER_BY_USER_ID: (userId: string) => `/producers/user/${userId}`,
    PRODUCER_ASSIGNED_BOOKS: (producerId: string) => `/producers/${producerId}/assigned-books`,
    PRODUCER_MARK_COMPLETED: (producerId: string) => `/producers/${producerId}/assigned-books/mark-completed`,

    // Production Assignments
    PRODUCTION_BOOKS: '/production/books',
    PRODUCTION_ASSIGN: '/production/assignments/assign',
    PRODUCTION_REASSIGN: '/production/assignments/reassign',
    PRODUCTION_RETRY_TRANSFER: (assignmentId: string) => `/production/assignments/${assignmentId}/retry-transfer`,

    // Statistics
    STATISTICS_USERS: '/statistics/users',

    // Backoffice Contracts (list, detail, download)
    CONTRACTS: '/contracts',
    CONTRACT_BY_ID: (contractId: string) => `/contracts/${contractId}`,
    CONTRACT_DOWNLOAD: (contractId: string) => `/contracts/${contractId}/download`,

    // Contract Reviewer
    CONTRACT_REVIEWER_TASKS: '/contract-reviewer/tasks',
    CONTRACT_REVIEWER_TASKS_COMPLETED: '/contract-reviewer/tasks/completed',
    CONTRACT_REVIEWER_TASK_BY_ID: (id: string) => `/contract-reviewer/tasks/${id}`,
    CONTRACT_REVIEWER_TASK_CLAIM: (id: string) => `/contract-reviewer/tasks/${id}/claim`,
    CONTRACT_REVIEWER_TASK_UNCLAIM: (id: string) => `/contract-reviewer/tasks/${id}/unclaim`,
    CONTRACT_REVIEWER_TASK_APPROVE: (id: string) => `/contract-reviewer/tasks/${id}/approve`,
    CONTRACT_REVIEWER_TASK_REJECT: (id: string) => `/contract-reviewer/tasks/${id}/reject`,
    CONTRACT_REVIEWER_DOC_APPROVE: (id: string, docId: string) => `/contract-reviewer/tasks/${id}/documents/${docId}/approve`,
    CONTRACT_REVIEWER_DOC_REJECT: (id: string, docId: string) => `/contract-reviewer/tasks/${id}/documents/${docId}/reject`,
  },

  // ==================== CMS Service Endpoints ====================
  CMS: {
    CONTENT: '/cms/content',
    EXPORT_EN: '/cms/export/en',
    EXPORT_AR: '/cms/export/ar',
  },
} as const;

/**
 * Storage keys for localStorage/sessionStorage
 * Note: User data is stored in AuthContext (React state), not localStorage
 * Only tokens are persisted to localStorage
 */
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
} as const;
